/**
 * Material slot surface visualization — tints faces by per-face material
 * slot index while in edit mode. Optional, gated by
 * `editMode.showMaterialSlotColors`.
 *
 * Membership semantics: `faceAttributes.materialIndex[f]` directly addresses
 * the slot, with slot 0 implied for faces beyond the stored array length.
 * Each slot gets its own tint so users can visually distinguish multi-slot
 * assignments without reading the inspector.
 *
 * Rendering follows `VertexGroupColorOverlay`: one `<mesh>` per non-empty
 * slot bucket sharing a single live position attribute.
 */

import { useEffect, useMemo } from "react";
import * as THREE from "three/webgpu";
import { useResolvedLiveEditGeometry } from "../app/useLiveEditPreview.ts";
import type { PolygonTopology, SceneObject } from "../modeling/types.ts";
import { updateSharedVertexPositions } from "./EditModeRenderer.tsx";
import { createOverlayFaceMaterial } from "./editDots.ts";
import { groupColorAt } from "./groupColors.ts";
import { useDisposeMany } from "./hooks/useDispose.ts";

interface SlotBucket {
  slotIdx: number;
  indices: Uint32Array;
}

function bucketFacesBySlot(
  topology: PolygonTopology,
  materialIndex: Uint16Array | undefined,
  slotCount: number,
  hiddenFaces: Set<number>,
): SlotBucket[] {
  const { offsets, corners } = topology;
  const faceCount = offsets.length === 0 ? 0 : offsets.length - 1;
  if (faceCount === 0 || slotCount === 0) return [];

  const triCounts = new Uint32Array(slotCount);
  for (let f = 0; f < faceCount; f++) {
    if (hiddenFaces.has(f)) continue;
    const slot =
      materialIndex && f < materialIndex.length ? materialIndex[f] : 0;
    if (slot >= slotCount) continue;
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCounts[slot] += size - 2;
  }

  const buckets: SlotBucket[] = [];
  const writers: Uint32Array[] = [];
  const writePositions = new Uint32Array(slotCount);
  for (let s = 0; s < slotCount; s++) {
    if (triCounts[s] === 0) continue;
    const buf = new Uint32Array(triCounts[s] * 3);
    writers[s] = buf;
    buckets.push({ slotIdx: s, indices: buf });
  }

  for (let f = 0; f < faceCount; f++) {
    if (hiddenFaces.has(f)) continue;
    const slot =
      materialIndex && f < materialIndex.length ? materialIndex[f] : 0;
    if (slot >= slotCount) continue;
    const start = offsets[f];
    const end = offsets[f + 1];
    const size = end - start;
    if (size < 3) continue;
    const buf = writers[slot];
    if (!buf) continue;
    let w = writePositions[slot];
    const v0 = corners[start];
    for (let i = 1; i < size - 1; i++) {
      buf[w++] = v0;
      buf[w++] = corners[start + i];
      buf[w++] = corners[start + i + 1];
    }
    writePositions[slot] = w;
  }
  return buckets;
}

interface MaterialSlotColorOverlayProps {
  objectId: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  materialSlots: SceneObject["materialSlots"];
  faceMaterialIndex: Uint16Array | undefined;
  hiddenFaces: Set<number>;
  dirtyVertices: Uint32Array | null;
  xray: boolean;
}

export function MaterialSlotColorOverlay({
  objectId,
  vertices,
  topology,
  materialSlots,
  faceMaterialIndex,
  hiddenFaces,
  dirtyVertices,
  xray,
}: MaterialSlotColorOverlayProps) {
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;

  const buckets = useMemo(
    () =>
      bucketFacesBySlot(
        topology,
        faceMaterialIndex,
        materialSlots.length,
        hiddenFaces,
      ),
    [topology, faceMaterialIndex, materialSlots.length, hiddenFaces],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: depend on length only — array reference changes every drag frame, but the GPU buffer must persist
  const positionAttr = useMemo(
    () => new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    [previewVertices.length],
  );

  const bucketGeos = useMemo(() => {
    const geos: THREE.BufferGeometry[] = [];
    for (const b of buckets) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", positionAttr);
      geo.setIndex(new THREE.BufferAttribute(b.indices, 1));
      geos.push(geo);
    }
    return geos;
  }, [buckets, positionAttr]);
  useDisposeMany(...bucketGeos);

  // Offset slot palette to keep VG / face-group / material overlays distinct
  // when stacked.
  const bucketMats = useMemo(
    () =>
      buckets.map((b) =>
        createOverlayFaceMaterial({
          color: `#${groupColorAt(b.slotIdx + 32).getHexString()}`,
          opacity: 0.55,
          depthTest: !xray,
          side: xray ? THREE.DoubleSide : THREE.FrontSide,
        }),
      ),
    [buckets, xray],
  );
  useDisposeMany(...bucketMats);

  useEffect(() => {
    if (positionAttr.array.length === previewVertices.length) {
      updateSharedVertexPositions(
        positionAttr,
        previewVertices,
        previewDirtyVertices,
        previewDirtyPositions,
      );
    }
  }, [
    positionAttr,
    previewVertices,
    previewDirtyVertices,
    previewDirtyPositions,
  ]);

  if (bucketGeos.length === 0) return null;

  return (
    <>
      {bucketGeos.map((geo, i) => (
        <mesh
          // biome-ignore lint/suspicious/noArrayIndexKey: buckets are stable per memo
          key={i}
          geometry={geo}
          material={bucketMats[i]}
          renderOrder={0}
        />
      ))}
    </>
  );
}
